/**
 * Загрузка глоссария из файла (сервер: routes/projects/glossaryContract.ts — importDraftSchema, decisionsSchema;
 * routes/projects/glossaryImports.ts). Ответы проверяются схемой, как sheetModel.
 */
import { z } from 'zod';

const draftOut = z.object({ key: z.string(), word: z.string(), stress: z.array(z.number().int().min(0)), in_book: z.number().int().min(0) });
/** duplicate — повтор в самом файле; in_book / in_shared — слово строки уже записано (то же ударение или запись другого вида) */
export const skipReasonSchema = z.enum(['heading', 'duplicate', 'in_book', 'in_shared', 'one_syllable', 'no_stress', 'no_pronunciation']);
const draftQuestion = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('conflict'), key: z.string(), scope: z.enum(['book', 'shared']), before: z.string(), file: z.string(), default: z.literal('file'),
    // Другие слова строки, уже записанные иначе: ответ относится и к ним; before/file — все слова через запятую
    also: z.array(z.string()).optional(),
  }),
  z.object({
    kind: z.literal('spelling'), key: z.string(), file: z.string(), book: z.string(), book_count: z.number().int().min(0),
    book_out: draftOut, default: z.literal('book'),
  }),
]);
const draftForm = z.object({
  entry_key: z.string(), key: z.string(), form: z.string(), stress: z.array(z.number().int().min(0)).max(1),
  origin: z.enum(['carried', 'manual', 'dictionary']), count: z.number().int().min(0),
  /** Словарь дал несколько ударений — варианты для показа (сервер: glossaryContract draftForm) */
  choices: z.array(z.string()).optional(),
  /** Словарная форма: ударение записи, под которое выбрана статья (сервер: glossaryContract draftForm) */
  head: z.array(z.number().int().min(0)).optional(),
});
/** Слово строки, которое не запишется, хотя строка пишется */
const droppedWord = z.object({ word: z.string(), reason: skipReasonSchema.extract(['one_syllable', 'no_stress', 'duplicate', 'in_book', 'in_shared']) });
const draftRow = z.object({
  n: z.number().int(), file: z.number().int().min(0), source: z.array(z.string()), kind: z.enum(['stress', 'pronunciation']),
  out: z.array(draftOut), pronunciation: z.string().optional(), skip: skipReasonSchema.optional(),
  question: draftQuestion.optional(), forms: z.array(draftForm).optional(), dropped: z.array(droppedWord).optional(),
});
export const importDraftSchema = z.object({
  v: z.literal(1),
  text_ready: z.boolean(),
  files: z.array(z.object({ file_id: z.string(), name: z.string(), lines: z.number().int().min(0) })),
  rows: z.array(draftRow),
});

export const importStatusSchema = z.enum(['parsing', 'ready', 'failed', 'applied', 'cancelled']);
const importCounts = z.object({ entries: z.number().int().min(0), forms: z.number().int().min(0) });
export const glossaryImportSchema = z.object({
  import_id: z.string(),
  status: importStatusSchema,
  format: z.enum(['template', 'freeform']),
  file_name: z.string(),
  /** null — черновика ещё нет или он старой версии */
  draft: importDraftSchema.nullable(),
  result: importCounts.nullable(),
});
export const importResponseSchema = z.object({ import: glossaryImportSchema });
export const applyResponseSchema = z.object({ result: importCounts });

/** Событие project:glossaryImportProgress (сервер: realtime/events.ts). */
export const importProgressSchema = z.object({
  projectId: z.string(),
  importId: z.string(),
  status: importStatusSchema,
});
/** Событие project:glossaryImported; кто не видит проект, получает только action и shared. */
export const importedEventSchema = z.object({
  projectId: z.string().optional(),
  importId: z.string().optional(),
  action: z.enum(['applied', 'reverted']),
  shared: z.boolean(),
});

/** Ответ на вопрос строки: конфликт — before | file, написание — file | book. */
export type ImportAnswer = 'file' | 'before' | 'book';
export type RowDecision = {
  n: number;
  file: number;
  skip?: boolean;
  answer?: ImportAnswer;
  /** word — слово, если выбрали «ё» вместо «е» (сервер принимает только такую замену) */
  fixed?: Array<{ key: string; stress: number[]; word?: string }>;
  pronunciation?: string;
  scope?: 'book' | 'shared';
  forms?: Array<{ key: string; on: boolean; stress: number[] }>;
};
export type ImportDecisions = { rows: RowDecision[] };

export type DraftOut = z.infer<typeof draftOut>;
export type DraftQuestion = z.infer<typeof draftQuestion>;
export type DraftForm = z.infer<typeof draftForm>;
export type DraftRow = z.infer<typeof draftRow>;
export type DroppedWord = z.infer<typeof droppedWord>;
export type SkipReason = z.infer<typeof skipReasonSchema>;
export type ImportDraft = z.infer<typeof importDraftSchema>;
export type ImportStatus = z.infer<typeof importStatusSchema>;
export type GlossaryImport = z.infer<typeof glossaryImportSchema>;
export type ImportCounts = z.infer<typeof importCounts>;
export type ImportProgress = z.infer<typeof importProgressSchema>;
